// pruebas
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  Save, Shield, ShieldCheck, AlertCircle, User, Upload, FileText, ArrowRight, ArrowLeft, Plus,
  Trash2, CheckCircle, Phone, Mail, MapPin, DollarSign, RotateCcw, X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import API_BASE_URL from '../Config';
import { TipoContacto, TipoContactoLabels } from '../enums/TipoContacto';
import SocioSearch from './SocioSearch';
import SocioServiciosSection from './socio-components/SocioServiciosSection';

const Field = ({ label, children, className = "" }) => (
  <div className={`enterprise-field ${className}`}>
    {children}
    <label>{label}</label>
  </div>
);

const SearchableSocioSelector = ({ value, onChange, label, token, placeholder = "Buscar por nombre, CI o Nro. Socio..." }) => {
  const [term, setTerm] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [selectedSocio, setSelectedSocio] = React.useState(null);
  const [showDropdown, setShowDropdown] = React.useState(false);

  React.useEffect(() => {
    if (value && typeof value === 'number') {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      fetch(`${API_BASE_URL}/socio/${value}`, { headers })
        .then(res => res.json())
        .then(data => {
          const nombre = data.personaFisica ? `${data.personaFisica.nombres} ${data.personaFisica.apellidos}` : data.personaJuridica?.razonSocial;
          setSelectedSocio({ id: data.id, nombreCompleto: nombre, numeroSocio: data.numeroSocio, numeroDocumento: data.personaFisica?.cedula || data.personaJuridica?.ruc });
        }).catch(err => console.error("Error loading selected socio:", err));
    } else if (!value) {
      setSelectedSocio(null);
    }
  }, [value, token]);

  const handleSearch = async (val) => {
    setTerm(val);
    if (val.length < 2) { setResults([]); setShowDropdown(false); return; }
    setLoading(true);
    setShowDropdown(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_BASE_URL}/socio/buscar?q=${val}`, { headers });
      if (res.ok) {
        const data = await res.json();
        const mapped = data.map(s => ({
          id: s.id,
          numeroSocio: s.numeroSocio,
          nombreCompleto: s.personaFisica ? `${s.personaFisica.nombres} ${s.personaFisica.apellidos}` : s.personaJuridica?.razonSocial,
          numeroDocumento: s.personaFisica?.cedula || s.personaJuridica?.ruc,
          tipoSocio: s.tipoSocio,
          estado: s.estado
        }));
        setResults(mapped);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  return (
    <div className="relative enterprise-field group">
      <div className="absolute left-4 top-1/2 -translate-y-1/2 z-10">
        <Plus className={`w-4 h-4 text-slate-300 transition-transform ${loading ? 'animate-spin' : ''}`} />
      </div>
      <input
        type="text"
        value={showDropdown ? term : (selectedSocio ? `${selectedSocio.nombreCompleto} (Nro. Socio ${selectedSocio.numeroSocio || 'S/N'})` : term)}
        onChange={e => handleSearch(e.target.value)}
        onFocus={() => { if (term.length >= 2) setShowDropdown(true); }}
        className="enterprise-input peer pl-12"
        placeholder=" "
      />
      <label className="ml-8">{label}</label>

      {showDropdown && (
        <>
          <div className="fixed inset-0 z-[50]" onClick={() => setShowDropdown(false)} />
          <div className="absolute z-[60] top-full left-0 w-full bg-white border border-slate-100 rounded-2xl shadow-2xl mt-2 overflow-hidden animate-premium-in max-h-60 overflow-y-auto ring-4 ring-slate-900/5">
            {results.map(s => {
              const isJunior = s.tipoSocio?.descripcion?.toUpperCase().includes('JUVENIL');
              const isActivo = s.estado?.id === 1;
              const isElegible = !isJunior && isActivo;

              return (
                <div
                  key={s.id}
                  onClick={() => {
                    if (!isElegible) {
                      Swal.fire({
                        title: 'Socio no elegible',
                        text: isJunior ? 'Un socio JUVENIL no puede ser proponente ni tutor.' : 'Solo socios ACTIVOS pueden ser proponentes o tutores.',
                        icon: 'warning',
                        confirmButtonColor: '#0F172A'
                      });
                      return;
                    }
                    onChange(s);
                    setSelectedSocio(s);
                    setShowDropdown(false);
                    setTerm('');
                  }}
                  className={`px-6 py-4 border-b border-slate-50 last:border-0 group/item transition-colors ${isElegible ? 'hover:bg-slate-50 cursor-pointer' : 'bg-slate-50/50 cursor-not-allowed opacity-60'
                    }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-[9px] font-black text-uni-teal uppercase tracking-widest mb-1 group-hover/item:text-slate-900 transition-colors">
                        Nro. Socio {s.numeroSocio || 'S/N'}
                      </div>
                      <div className="text-sm font-bold text-slate-800">{s.nombreCompleto}</div>
                      <div className="text-[10px] text-slate-400 font-bold uppercase">{s.numeroDocumento}</div>
                    </div>
                    {!isElegible && (
                      <span className="text-[8px] font-black bg-rose-100 text-rose-600 px-2 py-1 rounded uppercase tracking-tighter">
                        No Elegible
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            {term.length >= 2 && results.length === 0 && !loading && (
              <div className="p-8 text-center text-[10px] font-black text-slate-300 uppercase tracking-widest italic">
                No se encontraron socios elegibles
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const CountrySelector = ({ value, onChange, name }) => {
  const [isOpen, setIsOpen] = useState(false);
  const countries = [
    { code: 'PY', prefix: '+595', flag: 'py' },
    { code: 'AR', prefix: '+54', flag: 'ar' },
    { code: 'BR', prefix: '+55', flag: 'br' },
    { code: 'UY', prefix: '+598', flag: 'uy' }
  ];
  const selected = countries.find(c => c.prefix === value) || countries[0];

  return (
    <div className="relative">
      {/* Input oculto para activar la etiqueta flotante mediante peer */}
      <input type="text" className="peer absolute opacity-0 pointer-events-none" placeholder=" " readOnly value={selected.prefix} />

      <div
        onClick={() => setIsOpen(!isOpen)}
        className="enterprise-input flex items-center gap-3 px-4 cursor-pointer bg-slate-50/50 hover:bg-white transition-all group border border-slate-300"
      >
        <img
          src={`https://flagcdn.com/w40/${selected.flag}.png`}
          alt={selected.code}
          className="w-7 h-5 object-cover rounded shadow-md border border-white"
        />
        <span className="text-xs font-black text-slate-700 tracking-tighter">{selected.prefix}</span>
        <div className="ml-auto">
          <Plus className={`w-3 h-3 text-slate-300 transition-transform duration-300 ${isOpen ? 'rotate-45' : ''}`} />
        </div>
      </div>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute z-50 top-full left-0 mt-3 w-40 bg-white rounded-2xl shadow-2xl border border-slate-100 py-3 animate-premium-in ring-4 ring-slate-900/5">
            {countries.map(c => (
              <button
                key={c.code}
                type="button"
                onClick={() => {
                  onChange({ target: { name, value: c.prefix } });
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-4 px-5 py-3 hover:bg-uni-teal/5 group transition-all"
              >
                <img
                  src={`https://flagcdn.com/w40/${c.flag}.png`}
                  alt={c.code}
                  className="w-6 h-4 object-cover rounded shadow-sm group-hover:scale-110 transition-transform"
                />
                <div className="flex flex-col items-start">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{c.code}</span>
                  <span className="text-xs font-black text-slate-700">{c.prefix}</span>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};




export default function Socios() {
  const { user, token, hasPermission } = useAuth();
  const navigate = useNavigate();

  // --- CATALOGOS ---
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [generos, setGeneros] = useState([]);
  const [estadosCiviles, setEstadosCiviles] = useState([]);
  const [nacionalidades, setNacionalidades] = useState([]);
  const [tiposSocio, setTiposSocio] = useState([]);
  const [calificacionesRiesgo, setCalificacionesRiesgo] = useState([]);
  const [nivelesFidelidad, setNivelesFidelidad] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [tiposPersona] = useState([
    { id: 1, valor: 'FISICA', descripcion: 'Persona Física' },
    { id: 2, valor: 'JURIDICA', descripcion: 'Persona Jurídica' }
  ]);

  const [departamentos, setDepartamentos] = useState([]);
  const [ciudades, setCiudades] = useState([]);
  const [barrios, setBarrios] = useState([]);
  const [tiposDireccion, setTiposDireccion] = useState([]);
  const [profesiones, setProfesiones] = useState([]);
  const [rangoAntiguedades, setRangoAntiguedades] = useState([]);
  const [tiposIngreso, setTiposIngreso] = useState([]);
  const [parentescos, setParentescos] = useState([]);

  // --- WIZARD STATE ---
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 6;
  const steps = [
    { id: 1, title: 'ID', description: 'Identificación y Tipo' },
    { id: 2, title: 'Perfil', description: 'Laboral y Social' },
    { id: 3, title: 'Extra', description: 'Contactos' },
    { id: 4, title: 'Casa', description: 'Ubicación' },
    { id: 5, title: 'Herencia', description: 'Beneficiarios' },
    { id: 6, title: 'Papeles', description: 'Documentos' },
  ];


  // --- FORM DATA & PERSISTENCE ---
  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem('formData_socio');
    const initial = {
      tipoPersona: { id: 1, valor: 'FISICA' },
      nombres: '', apellidos: '', tipoDocumento: { id: '' }, numeroDocumento: '',
      fechaNacimiento: '', lugarNacimiento: '', genero: { id: '' }, estadoCivil: { id: '' },
      nacionalidad: { id: '' },
      departamentoId: '', ciudadId: '', barrioId: '', tipoDireccionId: '',
      nroCasa: '', direccionExacta: '', referencia: '',
      prefijoPrincipal: '+595', celularPrincipal: '',
      prefijoSecundario: '+55', celularSecundario: '',
      email: '', emailContacto: '', telefono: '',
      razonSocial: '', ruc: '', fechaConstitucion: '', representanteLegal: '',
      conyugeNombre: '', conyugeDocumento: '',
      tipoSocio: { id: '' }, calificacionRiesgo: { id: '' }, nivelFidelidad: { id: '' }, categoria: { id: '' },
      modalidadLaboral: 'DEPENDIENTE',
      datosLaborales: { empresa: '', profesionId: '', rangoAntiguedadId: '', direccion: '', telefono: '', actividadPrincipal: '', ruc: '' },
      perfilEconomico: [
        { tipoIngresoId: '', montoMensualPromedio: '', descripcionFuente: '', esActual: true, empresa: '', profesionId: '', direccion: '', telefono: '' },
        { tipoIngresoId: '', montoMensualPromedio: '', descripcionFuente: '', esActual: true, empresa: '', profesionId: '', direccion: '', telefono: '' }
      ],
      esPep: false,
      beneficiarios: [
        { nombreCompleto: '', numeroDocumento: '', parentescoId: '' },
        { nombreCompleto: '', numeroDocumento: '', parentescoId: '' }
      ],
      referencias: [],
      servicios: [],
      tutores: [],
      proponente: null
    };
    return saved ? { ...initial, ...JSON.parse(saved) } : initial;
  });

  useEffect(() => {
    localStorage.setItem('formData_socio', JSON.stringify(formData));
  }, [formData]);
  const resetForm = (confirm = true, keepPreview = false) => {
    const action = () => {
      localStorage.removeItem('formData_socio');
      setFormData({
        tipoPersona: { id: 1, valor: 'FISICA' },
        nombres: '', apellidos: '', tipoDocumento: { id: '' }, numeroDocumento: '',
        fechaNacimiento: '', genero: { id: '' }, estadoCivil: { id: '' },
        nacionalidad: { id: '' },
        departamentoId: '', ciudadId: '', barrioId: '', tipoDireccionId: '',
        nroCasa: '', direccionExacta: '', referencia: '',
        prefijoPrincipal: '+595', celularPrincipal: '',
        prefijoSecundario: '+55', celularSecundario: '',
        email: '', emailContacto: '', telefono: '',
        razonSocial: '', ruc: '', fechaConstitucion: '', representanteLegal: '',
        conyugeNombre: '', conyugeDocumento: '',
        tipoSocio: { id: '' }, calificacionRiesgo: { id: '' }, nivelFidelidad: { id: '' }, categoria: { id: '' },
        modalidadLaboral: 'DEPENDIENTE',
        datosLaborales: { empresa: '', profesionId: '', rangoAntiguedadId: '', direccion: '', telefono: '', actividadPrincipal: '', ruc: '' },
        perfilEconomico: [
          { tipoIngresoId: '', montoMensualPromedio: '', descripcionFuente: '', esActual: true, empresa: '', profesionId: '', direccion: '', telefono: '' },
          { tipoIngresoId: '', montoMensualPromedio: '', descripcionFuente: '', esActual: true, empresa: '', profesionId: '', direccion: '', telefono: '' }
        ],
        esPep: false,
        beneficiarios: [
          { nombreCompleto: '', numeroDocumento: '', parentescoId: '' },
          { nombreCompleto: '', numeroDocumento: '', parentescoId: '' }
        ],
        referencias: [],
        tutores: [],
        proponente: null
      });
      setCurrentStep(1);
      setCedulaFrontal(null);
      setCedulaDorso(null);
      setPreviewFrontal(null);
      setPreviewDorso(null);
      if (!keepPreview) setShowPreview(false);
    };


    if (confirm) {
      Swal.fire({
        title: '¿Nueva Solicitud?',
        text: "Se borrarán todos los datos cargados actualmente.",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#005f73',
        cancelButtonColor: '#94a3b8',
        confirmButtonText: 'Sí, limpiar todo',
        cancelButtonText: 'Cancelar'
      }).then((result) => {
        if (result.isConfirmed) action();
      });
    } else {
      action();
    }
  };


  // --- ARCHIVOS ---
  const [cedulaFrontal, setCedulaFrontal] = useState(null);
  const [cedulaDorso, setCedulaDorso] = useState(null);
  const [previewFrontal, setPreviewFrontal] = useState(null);
  const [previewDorso, setPreviewDorso] = useState(null);

  // --- PREVIEW MODAL STATE ---
  const [showPreview, setShowPreview] = useState(false);
  const [createdSocioId, setCreatedSocioId] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  // --- EFFECTS ---
  useEffect(() => {
    if (hasPermission('socios.write')) fetchCatalogos();
  }, [hasPermission]);

  const fetchCatalogos = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const responses = await Promise.all([
        fetch(`${API_BASE_URL}/catalogos/tipos-documento`, { headers }),
        fetch(`${API_BASE_URL}/catalogos/generos`, { headers }),
        fetch(`${API_BASE_URL}/catalogos/estados-civiles`, { headers }),
        fetch(`${API_BASE_URL}/catalogos/nacionalidades`, { headers }),
        fetch(`${API_BASE_URL}/ubicacion/departamentos`, { headers }),
        fetch(`${API_BASE_URL}/catalogos/tipos-direccion`, { headers }),
        fetch(`${API_BASE_URL}/catalogos/tipos-socio`, { headers }),
        fetch(`${API_BASE_URL}/catalogos/calificaciones-riesgo`, { headers }),
        fetch(`${API_BASE_URL}/catalogos/niveles-fidelidad`, { headers }),
        fetch(`${API_BASE_URL}/catalogos/profesiones`, { headers }),
        fetch(`${API_BASE_URL}/catalogos/rangos-antiguedad`, { headers }),
        fetch(`${API_BASE_URL}/catalogos/tipos-ingreso`, { headers }),
        fetch(`${API_BASE_URL}/catalogos/parentescos`, { headers }),
        fetch(`${API_BASE_URL}/catalogos/categorias`, { headers })
      ]);

      if (responses[0].ok) setTiposDocumento(await responses[0].json());
      if (responses[1].ok) setGeneros(await responses[1].json());
      if (responses[2].ok) setEstadosCiviles(await responses[2].json());
      if (responses[3].ok) setNacionalidades(await responses[3].json());
      if (responses[4].ok) setDepartamentos(await responses[4].json());
      if (responses[5].ok) setTiposDireccion(await responses[5].json());

      if (responses[6] && responses[6].ok) setTiposSocio(await responses[6].json());
      if (responses[7] && responses[7].ok) setCalificacionesRiesgo(await responses[7].json());
      if (responses[8] && responses[8].ok) setNivelesFidelidad(await responses[8].json());
      if (responses[9] && responses[9].ok) setProfesiones(await responses[9].json());
      if (responses[10] && responses[10].ok) setRangoAntiguedades(await responses[10].json());
      if (responses[11] && responses[11].ok) setTiposIngreso(await responses[11].json());
      if (responses[12] && responses[12].ok) setParentescos(await responses[12].json());
      if (responses[13] && responses[13].ok) setCategorias(await responses[13].json());

    } catch (err) { console.error("Error catálogos:", err); }
  };

  // --- CASCADING ADDRESS EFFECTS ---
  useEffect(() => {
    if (formData.departamentoId) {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      fetch(`${API_BASE_URL}/ubicacion/ciudades/${formData.departamentoId}`, { headers })
        .then(res => res.json())
        .then(data => setCiudades(data))
        .catch(err => console.error(err));
    } else {
      setCiudades([]);
    }
  }, [formData.departamentoId, token]);

  useEffect(() => {
    if (formData.ciudadId) {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      fetch(`${API_BASE_URL}/ubicacion/barrios/${formData.ciudadId}`, { headers })
        .then(res => res.json())
        .then(data => setBarrios(data))
        .catch(err => console.error(err));
    } else {
      setBarrios([]);
    }
  }, [formData.ciudadId, token]);

  // --- HANDLERS ---
  const formatGuarani = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const cleanValue = value.toString().replace(/\D/g, '');
    return cleanValue.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const parseGuarani = (value) => {
    return value.toString().replace(/\D/g, '');
  };

  const handleMontoChange = (index, val) => {
    const cleanVal = parseGuarani(val);
    updatePerfil(index, 'montoMensualPromedio', cleanVal);
  };

  const handleBlurValidation = async (field, value) => {
    if (!value || value.trim() === '') return;

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = new URLSearchParams();
      params.append(field, value);

      const resp = await fetch(`${API_BASE_URL}/socio/verificar-disponibilidad?${params.toString()}`, { headers });
      if (resp.ok) {
        const data = await resp.json();
        const availableKey = `${field}Disponible`;
        if (data[availableKey] === false) {
          const fieldLabel = field === 'documento' ? 'Número de Documento / RUC' :
            field === 'email' ? 'Correo Electrónico' : 'Celular';

          Swal.fire({
            title: 'Dato Duplicado',
            text: `El ${fieldLabel} "${value}" ya se encuentra registrado en el sistema.`,
            icon: 'error',
            confirmButtonColor: '#005f73'
          });

          // Opcionalmente, podemos limpiar el campo o marcarlo como error
          if (field === 'documento') setFormData(prev => ({ ...prev, numeroDocumento: '', ruc: '' }));
          if (field === 'email') setFormData(prev => ({ ...prev, email: '' }));
          if (field === 'celular') setFormData(prev => ({ ...prev, celularPrincipal: '' }));
        }
      }
    } catch (err) {
      console.error("Error validando disponibilidad:", err);
    }
  };

  const handleInputChange = (e, section = null) => {
    let { name, value, type, checked } = e.target;

    // --- VALIDACIÓN Y FORMATO AUTOMÁTICO ---
    if (name === 'numeroDocumento') {
      value = value.replace(/[^0-9]/g, '');
    }
    if (['nombres', 'apellidos', 'lugarNacimiento'].includes(name)) {
      value = value.toUpperCase();
    }

    setFormData(prev => {
      if (section === 'datosLaborales') {
        return {
          ...prev,
          datosLaborales: { ...prev.datosLaborales, [name]: value }
        };
      }

      if (name === 'tipoPersona') {
        const selected = tiposPersona.find(t => t.id === parseInt(value));
        return { ...prev, tipoPersona: selected || prev.tipoPersona };
      }

      if (['tipoDocumento', 'genero', 'estadoCivil', 'nacionalidad', 'tipoSocio', 'categoria'].includes(name)) {
        const idVal = value === '' ? '' : parseInt(value);
        return { ...prev, [name]: { id: idVal } };
      }

      return { ...prev, [name]: type === 'checkbox' ? checked : value };
    });
  };


  const addPerfil = () => {
    setFormData(prev => ({
      ...prev,
      perfilEconomico: [...prev.perfilEconomico, { tipoIngresoId: '', montoMensualPromedio: '', descripcionFuente: '', esActual: true }]
    }));
  };
  const updatePerfil = (index, field, value) => {
    setFormData(prev => {
      const newPerfil = [...prev.perfilEconomico];
      if (!newPerfil[index]) {
        newPerfil[index] = { tipoIngresoId: '', montoMensualPromedio: '', descripcionFuente: '', esActual: true };
      }
      newPerfil[index] = { ...newPerfil[index], [field]: value };
      return { ...prev, perfilEconomico: newPerfil };
    });
  };


  const removePerfil = (index) => {
    const updated = [...formData.perfilEconomico];
    updated.splice(index, 1);
    setFormData(prev => ({ ...prev, perfilEconomico: updated }));
  };

  const addBeneficiario = () => {
    setFormData(prev => ({
      ...prev,
      beneficiarios: [...prev.beneficiarios, { nombreCompleto: '', numeroDocumento: '', parentescoId: '' }]
    }));
  };
  const updateBeneficiario = (index, field, value) => {
    const updated = [...formData.beneficiarios];
    updated[index][field] = value;
    setFormData({ ...formData, beneficiarios: updated });
  };
  const removeBeneficiario = (index) => {
    const updated = [...formData.beneficiarios];
    updated.splice(index, 1);
    setFormData({ ...formData, beneficiarios: updated });
  };

  const handleFileChange = (e, setFile, setPreview) => {
    const file = e.target.files[0];
    if (file && ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
      setFile(file);
      setPreview(URL.createObjectURL(file));
    } else {
      Swal.fire('Error', 'Solo imágenes JPG/PNG.', 'error');
    }
  };

  const calculateAge = (birthday) => {
    if (!birthday) return 0;
    const diff = Date.now() - new Date(birthday).getTime();
    const ageDate = new Date(diff);
    return Math.abs(ageDate.getUTCFullYear() - 1970);
  };

  const isJuniorSocio = formData.tipoPersona.valor === 'FISICA' &&
    (tiposSocio.find(t => t.id === Number(formData.tipoSocio.id))?.descripcion?.toUpperCase().includes('JUVENIL') ||
      (formData.fechaNacimiento && calculateAge(formData.fechaNacimiento) < 18));

  const [tutorDoc, setTutorDoc] = useState('');
  const [loadingTutor, setLoadingTutor] = useState(false);

  const searchTutor = async () => {
    if (!tutorDoc) return;
    setLoadingTutor(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/socio/buscar?documento=${tutorDoc}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (resp.ok) {
        const results = await resp.json();
        const found = results.find(s => s.numeroDocumento === tutorDoc);
        if (found) {
          if (formData.tutores.some(t => t.tutorId === found.id)) {
            Swal.fire('Atención', 'Este tutor ya ha sido agregado.', 'info');
            return;
          }
          // Add tutor
          setFormData(prev => ({
            ...prev,
            tutores: [...prev.tutores, {
              tutorId: found.id,
              nombreCompleto: found.nombreCompleto,
              documento: found.numeroDocumento,
              parentescoId: '',
              esPrincipal: prev.tutores.length === 0
            }]
          }));
          setTutorDoc('');
        } else {
          Swal.fire('No encontrado', 'No se encontró un socio con ese documento.', 'warning');
        }
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Hubo un problema al buscar el tutor.', 'error');
    } finally {
      setLoadingTutor(false);
    }
  };

  const removeTutor = (index) => {
    setFormData(prev => ({
      ...prev,
      tutores: prev.tutores.filter((_, i) => i !== index)
    }));
  };

  const updateTutor = (index, field, value) => {
    setFormData(prev => {
      const newTutores = [...prev.tutores];
      newTutores[index] = { ...newTutores[index], [field]: value };
      return { ...prev, tutores: newTutores };
    });
  };

  const validateStep = () => {
    const isFisica = formData.tipoPersona.valor === 'FISICA';
    let missing = [];

    if (currentStep === 1) {
      if (isFisica) {
        if (!formData.nombres) missing.push('Nombres');
        if (!formData.apellidos) missing.push('Apellidos');
        if (!formData.numeroDocumento) missing.push('Nro Documento');
        if (!formData.fechaNacimiento) missing.push('Fecha de Nacimiento');
        if (!formData.genero.id) missing.push('Sexo');
        if (!formData.estadoCivil.id) missing.push('Estado Civil');
        if (!formData.nacionalidad.id) missing.push('Nacionalidad');

        // Validación obligatoria para casados
        if (estadosCiviles.find(e => e.id === Number(formData.estadoCivil.id))?.descripcion?.toUpperCase().includes('CASADO')) {
          if (!formData.conyugeNombre) missing.push('Nombre del Cónyuge');
          if (!formData.conyugeDocumento) missing.push('Documento del Cónyuge');
        }
      } else {
        if (!formData.razonSocial) missing.push('Razón Social');
        if (!formData.ruc) missing.push('RUC');
      }
    }

    if (currentStep === 2 && !isJuniorSocio) {
      if (!formData.perfilEconomico[0].empresa) missing.push('Empresa / Actividad Principal');
      if (!formData.perfilEconomico[0].montoMensualPromedio) missing.push('Ingreso Mensual');
    }

    if (currentStep === 3) {
      if (!formData.celularPrincipal) missing.push('Celular Principal');
    }
    if (currentStep === 4) {
      if (!formData.departamentoId) missing.push('Departamento');
      if (!formData.ciudadId) missing.push('Ciudad');
      if (!formData.direccionExacta) missing.push('Dirección Exacta');
    }

    if (isJuniorSocio && formData.tutores.length === 0) {
      missing.push('Al menos un Tutor Legal (Socio es menor de edad)');
    }


    if (missing.length > 0) {
      Swal.fire('Campos Obligatorios', `Faltan: ${missing.join(', ')}`, 'warning');
      return false;
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep()) setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!cedulaFrontal || !cedulaDorso) {
      Swal.fire('Documentos', 'Debe adjuntar Cédula (Frente y Dorso)', 'warning');
      return;
    }

    const isFisica = formData.tipoPersona.valor === 'FISICA';
    const payload = {
      tipoPersonaId: formData.tipoPersona.id,
      nombre: isFisica ? formData.nombres : null,
      apellido: isFisica ? formData.apellidos : null,
      numeroDocumento: isFisica ? formData.numeroDocumento : null,
      fechaNacimiento: isFisica ? formData.fechaNacimiento : null,
      lugarNacimiento: isFisica ? formData.lugarNacimiento : null,
      tipoDocumentoId: isFisica ? (formData.tipoDocumento.id || null) : null,
      generoId: isFisica ? (formData.genero.id || null) : null,
      estadoCivilId: isFisica ? (formData.estadoCivil.id || null) : null,
      conyugeNombre: formData.conyugeNombre,
      conyugeDocumento: formData.conyugeDocumento,
      razonSocial: !isFisica ? formData.razonSocial : null,
      ruc: !isFisica ? formData.ruc : null,
      fechaConstitucion: !isFisica ? formData.fechaConstitucion : null,
      representanteLegal: !isFisica ? formData.representanteLegal : null,
      nacionalidadId: formData.nacionalidad.id || null,
      tipoSocioId: formData.tipoSocio.id || null,
      categoriaId: formData.categoria.id || null,
      proponenteId: formData.proponente ? formData.proponente.id : null,
      email: formData.email, // <--- Agregar correo en la raiz
      esPep: formData.esPep,
      datosLaborales: {
        ...formData.datosLaborales,
        empresa: formData.perfilEconomico[0]?.empresa || formData.datosLaborales.empresa,
        profesionId: formData.perfilEconomico[0]?.profesionId || formData.datosLaborales.profesionId,
        direccion: formData.perfilEconomico[0]?.direccion || formData.datosLaborales.direccion,
        telefono: formData.perfilEconomico[0]?.telefono || formData.datosLaborales.telefono
      },
      referencias: formData.referencias,
      beneficiarios: formData.beneficiarios.filter(b => b.nombreCompleto && b.numeroDocumento),
      contactos: [
        { tipoContacto: 'EMAIL', valor: formData.email, esPrincipal: true },
        { tipoContacto: 'CELULAR', valor: `${formData.prefijoPrincipal} ${formData.celularPrincipal}`.trim(), esPrincipal: true },
        ...(formData.celularSecundario ? [{ tipoContacto: 'CELULAR', valor: `${formData.prefijoSecundario} ${formData.celularSecundario}`.trim(), esPrincipal: false }] : []),
        ...(formData.emailContacto ? [{ tipoContacto: 'EMAIL', valor: formData.emailContacto, esPrincipal: false }] : [])
      ],

      perfilEconomico: formData.perfilEconomico.filter(p => p.empresa || p.montoMensualPromedio).map((p) => {
        return {
          ...p,
          descripcionFuente: p.empresa || p.descripcionFuente,
          tipoIngresoId: p.tipoIngresoId || 1
        };
      }),
      ingreso: formData.perfilEconomico.reduce((acc, curr) => acc + (Number(curr.montoMensualPromedio) || 0), 0),

      departamentoId: formData.departamentoId,
      ciudadId: formData.ciudadId,
      barrioId: formData.barrioId,
      tipoDireccionId: formData.tipoDireccionId,
      nroCasa: formData.nroCasa,
      direccionExacta: formData.direccionExacta,
      referencia: formData.referencia,
      tutores: formData.tutores.map(t => ({
        tutorId: t.tutorId,
        parentescoId: t.parentescoId || null,
        esPrincipal: t.esPrincipal
      })),
      servicios: formData.servicios
    };

    const data = new FormData();
    data.append('socio', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
    if (cedulaFrontal) data.append('cedulaFrontal', cedulaFrontal);
    if (cedulaDorso) data.append('cedulaDorso', cedulaDorso);

    try {
      Swal.fire({ title: 'Procesando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      const resp = await fetch(`${API_BASE_URL}/socio`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: data
      });
      if (!resp.ok) throw new Error(await resp.text());
      const newSocio = await resp.json();
      setCreatedSocioId(newSocio.id);

      const pdfResp = await fetch(`${API_BASE_URL}/socio/contrato/${newSocio.id}/descargar`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (pdfResp.ok) {
        const blob = await pdfResp.blob();
        setPdfUrl(URL.createObjectURL(blob));
        setShowPreview(true);
        resetForm(false, true); // <--- Limpiar el fondo pero mantener el PDF abierto
        Swal.close();
      } else {

        Swal.fire('Advertencia', 'Socio creado, pero error al cargar la vista previa del contrato.', 'warning');
        navigate(`/socios/${newSocio.id}/verificar-solicitud`);
      }
    } catch (error) {
      Swal.fire('Error', `No se pudo registrar: ${error.message}`, 'error');
    }
  };

  const handleConfirmSignature = async () => {
    try {
      Swal.fire({ title: 'Confirmando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      const resp = await fetch(`${API_BASE_URL}/socio/${createdSocioId}/confirmar`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!resp.ok) throw new Error(await resp.text());
      resetForm(false); // Limpiar TODO el estado y localStorage
      Swal.fire('Éxito', 'Solicitud finalizada correctamente.', 'success').then(() => navigate('/'));

    } catch (error) { Swal.fire('Error', error.message, 'error'); }

  };

  const handlePrint = () => {
    const iframe = document.getElementById('pdf-frame');
    if (iframe) iframe.contentWindow.print();
  };

  const renderPreviewModal = () => (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-premium-in">
      <div className="enterprise-card w-full max-w-4xl h-[90vh] flex flex-col">
        <div className="bg-slate-900 text-white p-4 flex justify-between items-center">
          <h3 className="text-xs font-black uppercase tracking-widest flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" /> Solicitud Generada</h3>
          <button onClick={() => setShowPreview(false)} className="hover:bg-white/10 p-1 rounded transition-colors"><RotateCcw className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 bg-slate-100 p-6">
          {pdfUrl && <iframe id="pdf-frame" src={pdfUrl} className="w-full h-full rounded-lg shadow-inner bg-white" title="PDF"></iframe>}
        </div>
        <div className="p-4 bg-white border-t border-slate-100 flex justify-end gap-3">
          <button onClick={handlePrint} className="px-6 py-2 border border-slate-200 text-slate-600 rounded-enterprise text-xs font-bold hover:bg-slate-50">Imprimir</button>
          <button onClick={handleConfirmSignature} className="px-8 py-2 bg-emerald-600 text-white rounded-enterprise text-xs font-bold hover:bg-emerald-700 shadow-lg shadow-emerald-200">Confirmar Firma</button>
        </div>
      </div>
    </div>
  );

  // --- RENDER STEPS ---
  const renderStep1 = () => (
    <div className="space-y-10 animate-premium-in mt-4">
      {/* Selector de Tipo Persona */}
      <div className="flex gap-4 p-1 bg-slate-100 rounded-xl w-fit mb-10">
        {tiposPersona.map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => setFormData({ ...formData, tipoPersona: t })}
            className={`px-10 py-3 rounded-lg text-[11px] font-black uppercase tracking-widest transition-all ${formData.tipoPersona.id === t.id
              ? 'bg-white text-uni-teal shadow-md shadow-slate-200 scale-[1.02]'
              : 'text-slate-400 hover:text-slate-600'
              }`}
          >
            {t.descripcion}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-12">
        <div className="col-span-full border-b border-slate-100 pb-4 mb-2">
          <h3 className="text-[11px] font-black uppercase tracking-widest text-uni-teal">Clasificación de Socio</h3>
        </div>

        <div className="md:col-span-12">
          <Field label="Tipo de Socio">
            <select name="tipoSocio" value={formData.tipoSocio.id} onChange={handleInputChange} className="enterprise-input peer">
              <option value="">Seleccionar...</option>
              {tiposSocio.map(t => <option key={t.id} value={t.id}>{t.descripcion}</option>)}
            </select>
          </Field>
        </div>

        <div className="col-span-full border-b border-slate-100 pb-4 mt-4">
          <h3 className="text-[11px] font-black uppercase tracking-widest text-uni-teal">Identificación</h3>
        </div>

        {formData.tipoPersona.valor === 'FISICA' ? (
          <>
            <div className="md:col-span-3">
              <Field label="Tipo Documento*">
                <select name="tipoDocumento" value={formData.tipoDocumento.id} onChange={handleInputChange} className="enterprise-input peer">
                  <option value="">Seleccionar...</option>
                  {tiposDocumento.map(t => <option key={t.id} value={t.id}>{t.nombre_tipo || t.descripcion}</option>)}
                </select>
              </Field>
            </div>
            <div className="md:col-span-3">
              <Field label="Número de Documento*">
                <input
                  type="text"
                  name="numeroDocumento"
                  value={formData.numeroDocumento}
                  onChange={handleInputChange}
                  onBlur={(e) => handleBlurValidation('documento', e.target.value)}
                  className="enterprise-input peer"
                />
              </Field>
            </div>
            <div className="md:col-span-3">
              <Field label="Nombres*">
                <input type="text" name="nombres" value={formData.nombres} onChange={handleInputChange} className="enterprise-input peer" />
              </Field>
            </div>
            <div className="md:col-span-3">
              <Field label="Apellidos*">
                <input type="text" name="apellidos" value={formData.apellidos} onChange={handleInputChange} className="enterprise-input peer" />
              </Field>
            </div>

            <div className="md:col-span-4">
              <Field label="Fecha de Nacimiento*">
                <input type="date" name="fechaNacimiento" value={formData.fechaNacimiento} onChange={handleInputChange} className="enterprise-input peer" placeholder=" " />
              </Field>
            </div>
            <div className="md:col-span-4">
              <Field label="SEXO*">
                <select name="genero" value={formData.genero.id} onChange={handleInputChange} className="enterprise-input peer">
                  <option value="">Seleccionar...</option>
                  {generos.map(g => <option key={g.id} value={g.id}>{g.descripcion}</option>)}
                </select>
              </Field>
            </div>
            <div className="md:col-span-4">
              <Field label="ESTADO CIVIL*">
                <select name="estadoCivil" value={formData.estadoCivil.id} onChange={handleInputChange} className="enterprise-input peer">
                  <option value="">Seleccionar...</option>
                  {estadosCiviles.map(e => <option key={e.id} value={e.id}>{e.descripcion}</option>)}
                </select>
              </Field>
            </div>
            <div className="md:col-span-4">
              <Field label="NACIONALIDAD*">
                <select name="nacionalidad" value={formData.nacionalidad.id} onChange={handleInputChange} className="enterprise-input peer">
                  <option value="">Seleccionar...</option>
                  {nacionalidades.map(n => <option key={n.id} value={n.id}>{n.pais}</option>)}
                </select>
              </Field>
            </div>
            <div className="md:col-span-8">
              <Field label="Lugar de Nacimiento">
                <input
                  type="text"
                  name="lugarNacimiento"
                  value={formData.lugarNacimiento}
                  onChange={handleInputChange}
                  className="enterprise-input peer"
                />
              </Field>
            </div>

            {/* SECCIÓN CÓNYUGE (Si corresponde) */}
            {estadosCiviles.find(e => e.id === Number(formData.estadoCivil.id))?.descripcion?.toUpperCase().includes('CASADO') && (
              <div className="col-span-full mt-6 p-8 bg-uni-teal/5 rounded-2xl border border-uni-teal/10 animate-premium-in">
                <h4 className="text-[11px] font-black uppercase tracking-widest text-uni-teal mb-6">Datos del Cónyuge</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Nombre Completo Cónyuge*"><input type="text" name="conyugeNombre" value={formData.conyugeNombre} onChange={handleInputChange} className="enterprise-input peer" placeholder=" " /></Field>
                  <Field label="Documento Cónyuge*"><input type="text" name="conyugeDocumento" value={formData.conyugeDocumento} onChange={handleInputChange} className="enterprise-input peer" placeholder=" " /></Field>
                </div>
              </div>
            )}

            {/* SECCIÓN TUTORES PARA MENORES DE EDAD */}
            {isJuniorSocio && (
              <div className="col-span-full mt-10 p-10 bg-uni-teal/5 rounded-3xl border border-uni-teal/20 animate-premium-in shadow-xl shadow-uni-teal/5">
                <div className="flex items-center gap-4 mb-8">
                  <div className="p-3 bg-white rounded-2xl shadow-sm border border-uni-teal/20 text-uni-teal">
                    <ShieldCheck className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-[11px] font-black uppercase tracking-widest text-uni-teal">Tutores Legales y Adultos Responsables</h4>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Requerido para socios menores de edad</p>
                  </div>
                </div>

                <div className="space-y-8">
                  {/* Buscador de Tutores */}
                  <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                    <SearchableSocioSelector
                      label="Buscar Tutor por Nombre, Cédula o Nro. Socio"
                      token={token}
                      onChange={(found) => {
                        if (formData.tutores.some(t => t.tutorId === found.id)) {
                          Swal.fire('Atención', 'Este tutor ya ha sido agregado.', 'info');
                          return;
                        }
                        setFormData(prev => ({
                          ...prev,
                          tutores: [...prev.tutores, {
                            tutorId: found.id,
                            nombreCompleto: found.nombreCompleto,
                            documento: found.numeroDocumento,
                            parentescoId: '',
                            esPrincipal: prev.tutores.length === 0
                          }]
                        }));
                      }}
                    />
                  </div>

                  {/* Lista de Tutores Agregados */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {formData.tutores.map((t, idx) => (
                      <div key={idx} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm relative group animate-premium-in">
                        <button
                          type="button"
                          onClick={() => removeTutor(idx)}
                          className="absolute -top-2 -right-2 bg-rose-500 text-white p-2 rounded-xl shadow-lg opacity-0 group-hover:opacity-100 transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <div className="flex items-center gap-4 mb-6">
                          <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400">
                            <User className="w-6 h-6" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-black text-slate-900 truncate tracking-tight">{t.nombreCompleto}</p>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t.documento}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <select
                            value={t.parentescoId}
                            onChange={(e) => updateTutor(idx, 'parentescoId', e.target.value)}
                            className="text-[10px] font-black uppercase tracking-widest bg-slate-50 border-none rounded-lg px-3 py-2 w-full"
                          >
                            <option value="">Seleccionar Parentesco...</option>
                            {parentescos.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                          </select>
                          <button
                            type="button"
                            onClick={() => {
                              const newTutores = formData.tutores.map((tut, i) => ({ ...tut, esPrincipal: i === idx }));
                              setFormData({ ...formData, tutores: newTutores });
                            }}
                            className={`text-[9px] font-black uppercase tracking-widest rounded-lg px-3 py-2 transition-all ${t.esPrincipal
                              ? 'bg-uni-teal text-white shadow-md'
                              : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                              }`}
                          >
                            {t.esPrincipal ? 'Principal' : 'Marcar Principal'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {formData.tutores.length === 0 && (
                    <div className="text-center py-10 bg-slate-50/50 rounded-3xl border-2 border-dashed border-slate-200">
                      <p className="text-sm font-bold text-slate-400 italic">No se han registrado tutores responsables aún.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="md:col-span-8"><Field label="Razón Social"><input type="text" name="razonSocial" value={formData.razonSocial} onChange={handleInputChange} className="enterprise-input peer" placeholder=" " /></Field></div>
            <div className="md:col-span-4">
              <Field label="RUC">
                <input
                  type="text"
                  name="ruc"
                  value={formData.ruc}
                  onChange={handleInputChange}
                  onBlur={(e) => handleBlurValidation('documento', e.target.value)}
                  className="enterprise-input peer"
                  placeholder=" "
                />
              </Field>
            </div>
            <div className="md:col-span-4"><Field label="Fecha Constitución"><input type="date" name="fechaConstitucion" value={formData.fechaConstitucion} onChange={handleInputChange} className="enterprise-input peer" placeholder=" " /></Field></div>
            <div className="md:col-span-8"><Field label="Representante Legal"><input type="text" name="representanteLegal" value={formData.representanteLegal} onChange={handleInputChange} className="enterprise-input peer" placeholder=" " /></Field></div>
          </>
        )}

        {/* SECCIÓN SOCIO PROPONENTE */}
        <div className="col-span-full mt-10 p-10 bg-slate-50 rounded-3xl border border-slate-200 animate-premium-in shadow-xl shadow-slate-100/50">
          <div className="flex items-center gap-4 mb-8">
            <div className="p-3 bg-white rounded-2xl shadow-sm border border-slate-200 text-slate-500">
              <User className="w-6 h-6" />
            </div>
            <div>
              <h4 className="text-[11px] font-black uppercase tracking-widest text-slate-700">Socio Proponente</h4>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Busque y seleccione al socio que recomienda este ingreso</p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
              <SearchableSocioSelector
                label="Buscar Proponente por Nombre, Cédula o Nro. Socio"
                token={token}
                value={formData.proponente?.id}
                onChange={(socio) => setFormData(prev => ({ ...prev, proponente: socio }))}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => {
    return (
      <div className="space-y-12 animate-premium-in mt-6">
        <div className="col-span-full space-y-8">
          <div className="flex justify-between items-center bg-slate-50 p-8 rounded-[2rem] border border-slate-100 shadow-inner">
            <div>
              <h4 className="text-[12px] font-black text-uni-teal uppercase tracking-[0.25em] flex items-center gap-3">
                <DollarSign className="w-5 h-5" /> Perfil Laboral y Declaración de Ingresos
              </h4>
              <p className="text-[10px] text-slate-400 font-bold uppercase mt-2 tracking-widest">
                {isJuniorSocio
                  ? 'Especifique la fuente de sustento familiar o del tutor'
                  : 'Registre sus actividades económicas de forma independiente para mejorar su calificación'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, perfilEconomico: [...formData.perfilEconomico, { tipoIngresoId: '', montoMensualPromedio: '', descripcionFuente: '', esActual: true, empresa: '', profesionId: '', direccion: '', telefono: '' }] })}
              className="flex items-center gap-2 px-8 py-4 bg-white border border-slate-200 rounded-2xl text-[10px] font-black text-slate-600 uppercase tracking-[0.15em] hover:bg-slate-900 hover:text-white transition-all shadow-xl hover:scale-105 active:scale-95"
            >
              <Plus className="w-4 h-4" /> Agregar Actividad
            </button>
          </div>

          {isJuniorSocio && (
            <div className="p-6 bg-amber-50 border border-amber-100 rounded-2xl flex items-start gap-4 animate-premium-in">
              <AlertCircle className="w-6 h-6 text-amber-500 shrink-0" />
              <div>
                <p className="text-[11px] font-black text-amber-900 uppercase tracking-wider">Aviso para Socios Juveniles</p>
                <p className="text-[10px] font-bold text-amber-800/70 uppercase tracking-widest mt-1 leading-relaxed">
                  Los menores de edad no requieren demostrar ingresos. Use esta sección para detallar la actividad económica del tutor responsable.
                </p>
              </div>
            </div>
          )}

          <div className="space-y-12">
            {formData.perfilEconomico.map((pe, idx) => (
              <div key={idx} className="relative group p-12 bg-white rounded-[3rem] border border-slate-200 shadow-2xl shadow-slate-900/5 animate-premium-in hover:border-uni-teal/30 transition-colors">
                {idx > 0 && (
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, perfilEconomico: formData.perfilEconomico.filter((_, i) => i !== idx) })}
                    className="absolute -top-4 -right-4 w-12 h-12 bg-rose-500 text-white rounded-2xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-2xl z-20 hover:scale-110 active:scale-90"
                  >
                    <Trash2 className="w-6 h-6" />
                  </button>
                )}
                <div className="absolute -top-5 left-10 bg-slate-900 text-white px-8 py-3 rounded-2xl text-[11px] font-black uppercase tracking-[0.2em] shadow-2xl ring-4 ring-white">
                  Ingreso #{idx + 1}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-x-10 gap-y-12 mt-6">
                  <div className="md:col-span-4">
                    <Field label="Origen de Fondos / Modalidad">
                      <select value={pe.tipoIngresoId} onChange={e => { const updated = [...formData.perfilEconomico]; updated[idx].tipoIngresoId = e.target.value; setFormData({ ...formData, perfilEconomico: updated }); }} className="enterprise-input peer">
                        <option value="">Seleccionar...</option>
                        {tiposIngreso.map(ti => <option key={ti.id} value={ti.id}>{ti.nombre}</option>)}
                      </select>
                    </Field>
                  </div>
                  <div className="md:col-span-8">
                    <Field label="Nombre de Empresa / Negocio"><input type="text" value={pe.empresa} onChange={e => { const updated = [...formData.perfilEconomico]; updated[idx].empresa = e.target.value; setFormData({ ...formData, perfilEconomico: updated }); }} className="enterprise-input peer" placeholder=" " /></Field>
                  </div>

                  <div className="md:col-span-6">
                    <Field label="Cargo / Profesión">
                      <select value={pe.profesionId} onChange={e => { const updated = [...formData.perfilEconomico]; updated[idx].profesionId = e.target.value; setFormData({ ...formData, perfilEconomico: updated }); }} className="enterprise-input peer">
                        <option value="">Seleccionar...</option>
                        {profesiones.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                      </select>
                    </Field>
                  </div>
                  <div className="md:col-span-6">
                    <Field label="Monto Mensual Promedio (Gs.)">
                      <input
                        type="text"
                        value={formatGuarani(pe.montoMensualPromedio)}
                        onChange={e => {
                          const val = e.target.value.replace(/\D/g, '');
                          const updated = [...formData.perfilEconomico];
                          updated[idx].montoMensualPromedio = val;
                          setFormData({ ...formData, perfilEconomico: updated });
                        }}
                        className="enterprise-input peer font-black text-slate-800 text-lg !pl-10"
                      />
                      <DollarSign className="absolute left-4 top-4 w-5 h-5 text-emerald-500 pointer-events-none" />
                    </Field>
                  </div>

                  <div className="md:col-span-8">
                    <Field label="Dirección Laboral / Negocio"><input type="text" value={pe.direccion} onChange={e => { const updated = [...formData.perfilEconomico]; updated[idx].direccion = e.target.value; setFormData({ ...formData, perfilEconomico: updated }); }} className="enterprise-input peer" placeholder=" " /></Field>
                  </div>
                  <div className="md:col-span-4">
                    <Field label="Teléfono / Contacto"><input type="text" value={pe.telefono} onChange={e => { const updated = [...formData.perfilEconomico]; updated[idx].telefono = e.target.value; setFormData({ ...formData, perfilEconomico: updated }); }} className="enterprise-input peer" placeholder=" " /></Field>
                  </div>

                  <div className="md:col-span-12">
                    <Field label="Información Adicional / Observaciones">
                      <input type="text" value={pe.descripcionFuente} onChange={e => { const updated = [...formData.perfilEconomico]; updated[idx].descripcionFuente = e.target.value; setFormData({ ...formData, perfilEconomico: updated }); }} className="enterprise-input peer" placeholder=" " />
                    </Field>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start px-4">
          <div className="flex items-center gap-6 p-8 bg-amber-50 rounded-3xl border border-amber-100 shadow-sm hover:shadow-md transition-shadow">
            <input type="checkbox" name="esPep" checked={formData.esPep} onChange={(e) => setFormData({ ...formData, esPep: e.target.checked })} className="w-6 h-6 border-amber-300 rounded-lg text-uni-teal focus:ring-uni-teal cursor-pointer" />
            <div className="flex flex-col">
              <span className="text-[12px] font-black uppercase text-amber-900 tracking-wider">¿Es Persona Expuesta Políticamente (PEP)?</span>
              <span className="text-[10px] text-amber-600 font-bold uppercase mt-1">Declaro bajo fe de juramento si ocupo cargos públicos</span>
            </div>
          </div>
        </div>
      </div>
    );
  };


  const renderStep3 = () => (
    <div className="space-y-6 animate-premium-in mt-10">
      <div className="grid grid-cols-1 md:grid-cols-6 gap-x-6 gap-y-10">
        <div className="md:col-span-6 border-b border-slate-100 pb-4">
          <h3 className="text-[11px] font-black uppercase tracking-widest text-uni-teal">Información de Contacto</h3>
        </div>

        <div className="md:col-span-1">
          <Field label="País">
            <CountrySelector name="prefijoPrincipal" value={formData.prefijoPrincipal} onChange={handleInputChange} />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Celular Principal*">
            <input
              type="text"
              name="celularPrincipal"
              value={formData.celularPrincipal}
              onChange={handleInputChange}
              onBlur={(e) => {
                const fullPhone = `${formData.prefijoPrincipal} ${e.target.value}`.trim();
                handleBlurValidation('celular', fullPhone);
              }}
              className="enterprise-input peer"
              placeholder=" "
            />
          </Field>
        </div>
        <div className="md:col-span-3">
          <Field label="Email Principal">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              onBlur={(e) => handleBlurValidation('email', e.target.value)}
              className="enterprise-input peer"
              placeholder=" "
            />
          </Field>
        </div>

        <div className="md:col-span-6 mt-8 border-b border-slate-100 pb-4">
          <h3 className="text-[11px] font-black uppercase tracking-widest text-slate-400">Contacto Adicional</h3>
        </div>

        <div className="md:col-span-1">
          <Field label="País">
            <CountrySelector name="prefijoSecundario" value={formData.prefijoSecundario} onChange={handleInputChange} />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Celular Secundario">
            <input type="text" name="celularSecundario" value={formData.celularSecundario} onChange={handleInputChange} className="enterprise-input peer" placeholder=" " />
          </Field>
        </div>

        <div className="md:col-span-3">
          <Field label="Email Alternativo">
            <input type="email" name="emailContacto" value={formData.emailContacto} onChange={handleInputChange} className="enterprise-input peer" placeholder=" " />
          </Field>
        </div>
        <div className="md:col-span-3">
          <Field label="Teléfono Particular">
            <input type="text" name="telefono" value={formData.telefono} onChange={handleInputChange} className="enterprise-input peer" placeholder=" " />
          </Field>
        </div>
      </div>
    </div>
  );


  const renderStep4 = () => (
    <div className="space-y-6 animate-premium-in mt-10">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-10">
        <div className="col-span-full border-b border-slate-100 pb-4 mb-4">
          <h3 className="text-[11px] font-black uppercase tracking-widest text-uni-teal">Ubicación y Domicilio</h3>
        </div>

        <div className="md:col-span-4">
          <Field label="Departamento*">
            <select name="departamentoId" value={formData.departamentoId} onChange={e => handleInputChange({ target: { name: 'departamentoId', value: e.target.value } })} className="enterprise-input peer">
              <option value="">Seleccionar...</option>
              {departamentos.map(d => <option key={d.id} value={d.id}>{d.nombre}</option>)}
            </select>
          </Field>
        </div>
        <div className="md:col-span-4">
          <Field label="Ciudad*">
            <select name="ciudadId" value={formData.ciudadId} onChange={e => handleInputChange({ target: { name: 'ciudadId', value: e.target.value } })} className="enterprise-input peer" disabled={!formData.departamentoId}>
              <option value="">Seleccionar...</option>
              {ciudades.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </Field>
        </div>
        <div className="md:col-span-4">
          <Field label="Barrio">
            <select name="barrioId" value={formData.barrioId} onChange={e => handleInputChange({ target: { name: 'barrioId', value: e.target.value } })} className="enterprise-input peer" disabled={!formData.ciudadId}>
              <option value="">Seleccionar...</option>
              {barrios.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </select>
          </Field>
        </div>

        <div className="md:col-span-4">
          <Field label="Tipo de Dirección">
            <select name="tipoDireccionId" value={formData.tipoDireccionId} onChange={handleInputChange} className="enterprise-input peer">
              <option value="">Seleccionar...</option>
              {tiposDireccion.map(t => <option key={t.id} value={t.id}>{t.descripcion}</option>)}
            </select>
          </Field>
        </div>
        <div className="md:col-span-8">
          <Field label="Dirección Exacta*"><input type="text" name="direccionExacta" value={formData.direccionExacta} onChange={handleInputChange} className="enterprise-input peer" placeholder=" " /></Field>
        </div>
        <div className="md:col-span-4">
          <Field label="Nro de Casa"><input type="text" name="nroCasa" value={formData.nroCasa} onChange={handleInputChange} className="enterprise-input peer" placeholder=" " /></Field>
        </div>
        <div className="md:col-span-8">
          <Field label="Referencia / Cerca de..."><input type="text" name="referencia" value={formData.referencia} onChange={handleInputChange} className="enterprise-input peer" placeholder=" " /></Field>
        </div>
      </div>

      <div className="mt-16 pt-16 border-t border-slate-100">
        <SocioServiciosSection
          servicios={formData.servicios}
          onChange={(updated) => setFormData({ ...formData, servicios: updated })}
        />
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-8 animate-premium-in mt-10">
      <section className="space-y-4">
        <div className="flex justify-between items-center mb-2">
          <h4 className="text-[11px] font-black uppercase tracking-widest text-uni-teal">Perfil Económico (Ingresos)</h4>
          <button type="button" onClick={addPerfil} className="text-[10px] font-black uppercase text-uni-teal hover:text-white hover:bg-uni-teal bg-uni-teal/5 px-2 py-1 rounded transition-colors">+ Nueva Fuente</button>
        </div>
        <div className="space-y-6">
          {formData.perfilEconomico.map((p, i) => (
            <div key={i} className="relative group">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-10">
                <Field label="Tipo Ingreso">
                  <select value={p.tipoIngresoId} onChange={(e) => updatePerfil(i, 'tipoIngresoId', e.target.value)} className="enterprise-input peer">
                    <option value="">Seleccionar...</option>
                    {tiposIngreso.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
                  </select>
                </Field>
                <Field label="Monto Mensual"><input type="text" value={p.montoMensualPromedio} onChange={(e) => updatePerfil(i, 'montoMensualPromedio', e.target.value)} className="enterprise-input peer" placeholder=" " /></Field>
              </div>
              {formData.perfilEconomico.length > 1 && (
                <button type="button" onClick={() => removePerfil(i)} className="absolute top-0 -right-10 text-rose-500 opacity-0 group-hover:opacity-100 transition-opacity p-2"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );

  const renderStep6 = () => (
    <div className="space-y-6 animate-premium-in mt-10">
      <div className="flex justify-between items-center mb-6">
        <h4 className="text-[11px] font-black uppercase tracking-widest text-slate-400">Beneficiarios Designados</h4>
        <button type="button" onClick={addBeneficiario} className="text-[10px] font-black uppercase text-uni-teal hover:bg-uni-teal hover:text-white bg-uni-teal/5 px-4 py-2 rounded-full border border-uni-teal/20 transition-all">+ Agregar Beneficiario</button>
      </div>

      <div className="space-y-6">
        {formData.beneficiarios.map((ben, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-10 relative group">
            <Field label="Nombre Completo">
              <input type="text" value={ben.nombreCompleto} onChange={(e) => updateBeneficiario(i, 'nombreCompleto', e.target.value)} className="enterprise-input peer" />
            </Field>
            <Field label="Nro Documento">
              <input type="text" value={ben.numeroDocumento} onChange={(e) => updateBeneficiario(i, 'numeroDocumento', e.target.value)} className="enterprise-input peer" />
            </Field>
            <Field label="Parentesco">
              <select value={ben.parentescoId} onChange={(e) => updateBeneficiario(i, 'parentescoId', e.target.value)} className="enterprise-input peer">
                <option value="">Seleccionar...</option>
                {parentescos.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
            </Field>
            {formData.beneficiarios.length > 2 && (
              <button type="button" onClick={() => removeBeneficiario(i)} className="absolute -right-8 top-4 text-rose-500 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep7 = () => (
    <div className="space-y-8 animate-premium-in mt-10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className={`enterprise-card p-10 text-center border-dashed border-2 transition-colors ${previewFrontal ? 'border-uni-teal bg-uni-teal/5' : 'border-slate-200 bg-slate-50/50'}`}>
          <p className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-6">Cédula Frente</p>
          {previewFrontal ? (
            <div className="relative group mx-auto w-64 h-40">
              <img src={previewFrontal} alt="Frente" className="w-full h-full object-cover rounded-xl shadow-xl transition-all" />
              <button onClick={() => setPreviewFrontal(null)} className="absolute -top-3 -right-3 bg-rose-500 text-white p-2 rounded-full shadow-lg hover:scale-110 transition-transform"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center h-40 cursor-pointer group">
              <Upload className="w-10 h-10 text-slate-300 group-hover:text-uni-teal transition-colors mb-4" />
              <span className="text-[10px] font-black text-slate-400 group-hover:text-uni-teal">Subir archivo JPG/PNG</span>
              <input type="file" onChange={(e) => handleFileChange(e, setCedulaFrontal, setPreviewFrontal)} className="hidden" />
            </label>
          )}
        </div>
        <div className={`enterprise-card p-10 text-center border-dashed border-2 transition-colors ${previewDorso ? 'border-uni-teal bg-uni-teal/5' : 'border-slate-200 bg-slate-50/50'}`}>
          <p className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-6">Cédula Dorso</p>
          {previewDorso ? (
            <div className="relative group mx-auto w-64 h-40">
              <img src={previewDorso} alt="Dorso" className="w-full h-full object-cover rounded-xl shadow-xl transition-all" />
              <button onClick={() => setPreviewDorso(null)} className="absolute -top-3 -right-3 bg-rose-500 text-white p-2 rounded-full shadow-lg hover:scale-110 transition-transform"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center h-40 cursor-pointer group">
              <Upload className="w-10 h-10 text-slate-300 group-hover:text-uni-teal transition-colors mb-4" />
              <span className="text-[10px] font-black text-slate-400 group-hover:text-uni-teal">Subir archivo JPG/PNG</span>
              <input type="file" onChange={(e) => handleFileChange(e, setCedulaDorso, setPreviewDorso)} className="hidden" />
            </label>
          )}
        </div>
      </div>
    </div>
  );


  return (
    <div className="p-6 md:p-14 bg-slate-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <header className="mb-14">

          <div className="flex justify-between items-end mb-4">
            <h1 className="text-4xl font-black text-slate-800 tracking-tighter">Solicitud de Ingreso</h1>
            <button
              onClick={() => resetForm()}
              className="flex items-center gap-2 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-rose-500 hover:bg-rose-50 rounded-lg transition-all border border-rose-100"
            >
              <Trash2 className="w-3 h-3" /> Nueva Solicitud
            </button>
          </div>

        </header>

        <div className="enterprise-card bg-white relative p-6 md:p-14 shadow-2xl shadow-slate-200/50">
          <div className="flex items-center w-full mb-14">
            <h3 className="text-[13px] font-black uppercase tracking-widest text-uni-teal whitespace-nowrap pr-8">Paso {currentStep} - {steps[currentStep - 1].description}</h3>
            <div className="h-[1px] flex-1 bg-slate-100" />
          </div>

          <form onSubmit={handleSubmit} className="min-h-[450px]">
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}
            {currentStep === 5 && renderStep6()}
            {currentStep === 6 && renderStep7()}
          </form>


          <div className="mt-20 pt-10 border-t border-slate-50 flex justify-between items-center">
            {currentStep > 1 ? (
              <button type="button" onClick={prevStep} className="px-10 py-4 text-slate-400 text-[11px] font-black uppercase tracking-widest hover:text-uni-teal transition-colors flex items-center gap-3 group">
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" /> Anterior
              </button>
            ) : <div />}

            {currentStep < totalSteps ? (
              <button type="button" onClick={nextStep} className="px-12 py-4 bg-uni-teal text-white text-[11px] font-black uppercase tracking-[0.2em] rounded-full hover:bg-slate-900 transition-all shadow-xl shadow-uni-teal/20 flex items-center gap-3 group">
                Siguiente <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            ) : (
              <button onClick={handleSubmit} type="button" className="px-12 py-4 bg-uni-orange text-white text-[11px] font-black uppercase tracking-[0.2em] rounded-full hover:bg-slate-900 transition-all shadow-xl shadow-uni-orange/20 flex items-center gap-3 group">
                <Save className="w-4 h-4" /> Finalizar Solicitud
              </button>
            )}
          </div>
        </div>
      </div>

      {showPreview && renderPreviewModal()}


    </div>
  );
}